import { useTheme } from '@mui/material/styles';
import Select, { 
  StylesConfig, 
  components, 
  ControlProps, 
  MenuProps, 
  OptionProps, 
  MultiValueProps, 
  PlaceholderProps,
  GroupBase
} from 'react-select';
import { Typography, Box, Checkbox, FormControlLabel, Divider } from '@mui/material';
import { useState, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
  [key: string]: any; // Allow additional properties
}

interface MultiSelectProps {
  options: Option[];
  value: Option[];
  onChange: (selected: Option[]) => void;
  placeholder?: string;
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  compact?: boolean;
  customComponents?: {
    MultiValue?: React.ComponentType<any>;
    Option?: React.ComponentType<any>;
    [key: string]: React.ComponentType<any> | undefined;
  };
}

export function MultiSelect({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Select options...', 
  label,
  isLoading = false,
  disabled = false,
  icon,
  compact = false,
  customComponents = {}
}: MultiSelectProps) {
  const theme = useTheme();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  // Handle select all option
  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      // Select all options
      onChange(options);
    } else {
      // Deselect all options
      onChange([]);
    }
  };

  // Check if all options are selected
  const allSelected = options.length > 0 && value.length === options.length;

  // Custom Menu component with Select All option
  const Menu = (props: any) => {
    return (
      <components.Menu {...props}>
        {options.length > 0 && (
          <>
            <Box sx={{ 
              padding: compact ? '6px 8px' : '10px 12px',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    size={compact ? "small" : "medium"}
                    checked={allSelected}
                    indeterminate={value.length > 0 && value.length < options.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{ 
                      color: '#14bef0',
                      '&.Mui-checked': {
                        color: '#14bef0',
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: '#14bef0',
                      }
                    }}
                  />
                }
                label={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: compact ? '0.75rem' : '0.875rem',
                      fontWeight: 500
                    }}
                  >
                    Select All
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </Box>
            <Divider />
          </>
        )}
        {props.children}
      </components.Menu>
    );
  };

  const customStyles: StylesConfig<Option, true> = {
    control: (provided: any, state: ControlProps<Option, true>) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderColor: state.isFocused ? '#14bef0' : theme.palette.divider,
      boxShadow: state.isFocused ? '0 0 0 2px rgba(20, 190, 240, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#14bef0',
      },
      borderRadius: '8px',
      padding: compact ? '0px' : '2px',
      minHeight: compact ? '32px' : '42px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      borderRadius: '8px',
      overflow: 'hidden',
      zIndex: 9999,
      position: 'absolute',
      width: '100%',
      marginTop: '4px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    container: (provided) => ({
      ...provided,
      position: 'relative',
      zIndex: 9500,
      '&:hover': {
        zIndex: 9500,
      },
      '&:focus-within': {
        zIndex: 9500,
      },
    }),
    option: (provided: any, state: OptionProps<Option, true>) => ({
      ...provided,
      backgroundColor: state.isSelected 
        ? 'rgba(20, 190, 240, 0.1)'
        : state.isFocused 
          ? 'rgba(20, 190, 240, 0.05)'
          : 'transparent',
      color: state.isSelected ? '#14bef0' : theme.palette.text.primary,
      '&:active': {
        backgroundColor: 'rgba(20, 190, 240, 0.15)',
      },
      cursor: 'pointer',
      padding: compact ? '6px 8px' : '10px 12px',
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: 'rgba(20, 190, 240, 0.1)',
      borderRadius: '4px',
      padding: '0 2px',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#14bef0',
      fontSize: compact ? '0.75rem' : '0.875rem',
      padding: compact ? '1px' : '2px',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#14bef0',
      '&:hover': {
        backgroundColor: 'rgba(20, 190, 240, 0.2)',
        color: '#14bef0',
      },
      borderRadius: '0 4px 4px 0',
      padding: compact ? '1px' : '2px',
    }),
    placeholder: (provided: any, state: PlaceholderProps<Option>) => ({
      ...provided,
      color: theme.palette.text.secondary,
      fontSize: compact ? '0.75rem' : '0.875rem',
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme.palette.text.primary,
      fontSize: compact ? '0.75rem' : '0.875rem',
      margin: compact ? '0' : '2px',
      padding: compact ? '0' : '2px',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: compact ? '32px' : 'auto',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      padding: compact ? '4px' : '8px',
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      padding: compact ? '4px' : '8px',
    }),
  };

  const NoOptionsMessage = (props: any) => (
    <components.NoOptionsMessage {...props}>
      <Typography variant="body2" color="text.secondary" sx={{ 
        fontSize: compact ? '0.75rem' : '0.875rem' 
      }}>
        No options available
      </Typography>
    </components.NoOptionsMessage>
  );

  const LoadingMessage = (props: any) => (
    <components.LoadingMessage {...props}>
      <Typography variant="body2" color="text.secondary" sx={{ 
        fontSize: compact ? '0.75rem' : '0.875rem' 
      }}>
        Loading...
      </Typography>
    </components.LoadingMessage>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {label && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: compact ? 0.5 : 1, 
            fontWeight: 500, 
            fontSize: compact ? '0.75rem' : '0.875rem' 
          }}
        >
          {label}
        </Typography>
      )}
      <Select<Option, true>
        isMulti
        options={options}
        value={value}
        onChange={(selected) => onChange(Array.isArray(selected) ? selected : [])}
        styles={customStyles}
        placeholder={placeholder}
        isLoading={isLoading}
        isDisabled={disabled}
        components={{
          NoOptionsMessage,
          LoadingMessage,
          Menu,
          ...customComponents
        }}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />
    </Box>
  );
} 